import React, { useEffect, useRef, useState } from "react";
import styles from "./STHome.module.css";
import clsx from "clsx";
import { Icons } from "@/const/icons";
import Link from "next/link";
import {
  STButton,
  STInput,
  STSelect,
  STSwiper,
  STTextarea,
  STRotatingText,
  STTextAnimationFlip,
  STDrawer,
} from "..";
import Image from "next/image";
import {
  biomassInsightImg,
  BioMaterialImg,
  letsTalkImg,
} from "@/assets/images";
import { blogsNews, fundamentals, impactData } from "@/const/data";
import { SwiperSlide } from "swiper/react";
import { useContactUs, useBlogs } from "@/hooks";
import {
  companyLogo1,
  companyLogo2,
  companyLogo3,
  companyLogo4,
  companyLogo5,
  companyLogo6,
  companyLogo7,
  companyLogo8,
} from "../../assets/images";

export const STHome = () => {
  const LOGOS = [
    companyLogo1,
    companyLogo2,
    companyLogo3,
    companyLogo4,
    companyLogo5,
    companyLogo6,
    companyLogo7,
    companyLogo8,
  ];
  const [isCollapsed, setIsCollapsed] = useState(false);
  const {
    formData,
    loading,
    errors,
    success,
    handleChange,
    handleSelectChange,
    handleSubmit,
  } = useContactUs();
  const [bioMassDrawerOpen, setBioMassDrawerOpen] = useState(false);
  const [bioMaterialDrawerOpen, setBioMaterialDrawerOpen] = useState(false);
  const { blogs, fetchBlogs, fetchHomeBlog, homeBlog } = useBlogs();
  const latestBlog = homeBlog; // Use the featured home blog

  const [livePreviewData, setLivePreviewData] = useState(null);

  useEffect(() => {
    const handlePreviewMessage = (event) => {
      if (event.data?.type === "SUSTANIX_PREVIEW_DATA") {
        setLivePreviewData(event.data.data);
      }
    };
    window.addEventListener("message", handlePreviewMessage);
    return () => window.removeEventListener("message", handlePreviewMessage);
  }, []);

  useEffect(() => {
    fetchBlogs(1, 6);
    fetchHomeBlog();
  }, [fetchBlogs, fetchHomeBlog]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      if (scrollY > 250) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;

    const iconWidth = 52; // collapsed size (icon + padding)

    const updateWidth = () => {
      const contentWidth = contentRef.current.scrollWidth;

      if (isCollapsed) {
        setWidth(iconWidth);
      } else {
        if (window.innerWidth < 576) {
          setWidth(window.innerWidth - 24);
        } else {
          setWidth(contentWidth + 64); // spacing adjust
        }
      }
    };

    updateWidth();

    // observe resize (important for responsive)
    const observer = new ResizeObserver(updateWidth);
    observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, [isCollapsed, latestBlog]);
  return (
    <>
      {/* HERO SECTION START */}
      <section
        className={clsx(
          styles.heroSection,
          "d-flex align-items-end position-relative",
        )}
      >
        <div
          className={clsx(
            styles.heroInner,
            "w-100 d-flex justify-content-xl-between align-items-xl-end flex-column flex-xl-row gap-md",
          )}
        >
          {/* LEFT - TITLE */}
          <div className={clsx(styles.heroContent, "z-1")}>
            <STTextAnimationFlip
              className={clsx(styles.heroTitle, "w-100")}
              texts={[
                livePreviewData?.title1,
                livePreviewData?.title2,
                livePreviewData?.title3,
                livePreviewData?.title4,
              ].filter(Boolean)}
              subTitle={livePreviewData?.title5}
            />
          </div>

          {/* RIGHT - NOTIFICATION */}
          {latestBlog?.name && <div
            ref={wrapperRef}
            style={{ width: `${width}px` }}
            className={clsx(
              styles.notificationWrapper,
              isCollapsed && styles.collapsed,
              "bg-clr-white d-flex align-items-center gap-3 z-1 overflow-hidden",
            )}
          >
            <div
              className={clsx(
                styles.notificationIconWrapper,
                "d-flex align-items-center justify-content-center rounded-circle text-clr-bloodthirsty-warlock flex-shrink-0",
              )}
            >
              {Icons.notificationIcon}
            </div>

            <div
              ref={contentRef}
              className={clsx(
                styles.notificationContent,
                "d-flex flex-column flex-md-row align-items-md-center gap-1 gap-md-3",
              )}  
            >
              <p className="mb-0 p fw-medium">
                {latestBlog?.name || latestBlog?.title}
              </p>

              <Link
                href={
                  latestBlog?.slug
                    ? `/news-feed/${latestBlog.slug}`
                    : latestBlog?.href || "/news-feed"
                }
                className="text-clr-blue p mb-0 fw-medium d-flex align-items-center gap-2 flex-shrink-0"
              >
                Click to read {Icons.arrowDoubleRightIcon}
              </Link>
            </div>
          </div>
          }
        </div>
        {/* video or image background */}
        <div
          className={clsx(
            styles.videoWrapper,
            "position-absolute top-0 start-0 w-100 h-100",
          )}
        >
          {(!livePreviewData || livePreviewData?.heroIsVideo) ? (
            <video
              src={livePreviewData?.heroCoverPhoto || "/video/combined_video.mp4"}
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              disablePictureInPicture
              style={{ pointerEvents: "none" }}
              className={clsx(styles.heroVideo, "w-100 h-100 object-fit-cover")}
            />
          ) : (
            <img
              src={livePreviewData?.heroCoverPhoto}
              alt="Hero Cover"
              className="w-100 h-100 object-fit-cover"
            />
          )}
        </div>
      </section>
      {/* HERO SECTION END */}

      {/* BIO MATERIALS SECTION START */}
      <section
        className={clsx(
          styles.bioMaterialsSection,
          "position-relative space-xl-py",
        )}
      >
        <div className="container">
          <div
            className={clsx(
              styles.bioMaterialsWrapper,
              "d-flex gap-md justify-content-between align-items-center flex-column-reverse flex-lg-row",
            )}
          >
            <div
              className={clsx(
                styles.bioMaterialsContent,
                "d-flex flex-column align-items-start",
              )}
            >
              <h2 className="primary-title text-clr-black mb-0">
                {livePreviewData?.section2Title || (
                  <>
                    Bio-materials invented for{" "}
                    <span className="text-clr-black">sheer performance</span>
                  </>
                )}
              </h2>
              <p className="paragraph-text space-sm-mt space-md-mb">
                {livePreviewData?.section2Description ||
                  "We research and manufacture novel bio-based molecules to make polymers & formulations that replace fossil-based materials. Industries can move towards greener solution without compromise on economics or scale."}
              </p>
              <STButton
                suffix={Icons.arrowDoubleRightIcon}
                variant="outline-primary"
                onClick={() => setBioMaterialDrawerOpen(true)}
              >
                Learn more
              </STButton>
            </div>
            <div
              className={clsx(
                styles.bioMaterialsImageWrapper,
                "rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden",
              )}
            >
              <Image
                src={livePreviewData?.section2Photo || BioMaterialImg}
                alt="Bio Materials"
                width={360}
                height={360}
                className="rounded-circle object-cover w-100 h-100"
              />
            </div>
          </div>
        </div>
      </section>
      {/* BIO MATERIALS SECTION END */}

      {/* FUNDAMENTALS SECTION START */}
      <section className={clsx(styles.fundamentalsSection, "space-lg-pt")}>
        <div className="container">
          <div
            className={clsx(
              styles.fundamentalheader,
              "d-flex flex-column align-items-center space-lg-mb text-center",
            )}
          >
            <h2 className="primary-title text-clr-white mb-0">
              Three fundamentals :
            </h2>
            <h2 className="primary-title text-clr-gray mb-0">
              scalable, drop-in and truly circular
            </h2>
          </div>

          <div
            className={clsx(styles.fundamentalcardsWrapper, "d-grid gap-md")}
          >
            {(livePreviewData?.section3Cards || fundamentals).map((item, index) => (
              <div
                key={index}
                className={clsx(
                  styles.fundamentalcard,
                  "bg-off-white position-relative radius-lg space-md-p cursor-pointer transition-smooth",
                )}
              >
                <h2
                  className={clsx(
                    styles.cardIndex,
                    "h1 big space-md-mb fw-light transition-smooth",
                  )}
                >
                  0{index + 1}
                </h2>

                <div className={styles.fundamentalcardContent}>
                  <span
                    className={clsx(
                      styles.fundamentalcardIcon,
                      "space-sm-mb d-block transition-smooth fs-2",
                    )}
                  >
                    {typeof item.icon === "string" &&
                    (item.icon.startsWith("http") ||
                      item.icon.startsWith("/") ||
                      item.icon.startsWith("data:")) ? (
                      <img
                        src={item.icon}
                        alt={item.title}
                        style={{
                          width: "48px",
                          height: "48px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      item.icon
                    )}
                  </span>
                  <h3 className="fw-normal mb-2 transition-smooth h4">
                    {item.title}
                  </h3>
                  <p className={clsx("paragraph-text transition-smooth mb-0")}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FUNDAMENTALS SECTION END */}

      {/* BIOMASS INSIGHT SECTION START */}
      <section className={clsx(styles.biomassInsightSection, "space-lg-pt")}>
        <div className="container">
          <div className="radius-xl bg-off-white">
            <div
              className={clsx(
                styles.biomassInsightHeader,
                "d-flex justify-content-between align-items-start gap-3",
              )}
            >
              <div>
                <h2 className="primary-title mb-0">
                  Biomass is the new oil...
                </h2>
                <h2 className="primary-title text-clr-gray mb-0">
                  adding value to this nature’s ingredient
                </h2>
              </div>
              <span
                className="cursor-pointer hover-text"
                onClick={() => setBioMassDrawerOpen(true)}
              >
                {Icons.arrowRight}
              </span>
            </div>
            <div
              className={clsx(styles.biomassInsightImageWrapper, "space-sm-p ")}
            >
              <Image
                src={biomassInsightImg}
                alt="Biomass Insight"
                className="w-100 radius-md object-fit-cover"
              />
            </div>
          </div>
        </div>
      </section>
      {/* BIOMASS INSIGHT SECTION END */}

      {/* IMPACT SECTION START */}
      <section
        className={clsx(
          styles.impactSection,
          " space-xl-py d-flex gap-md flex-column flex-lg-row",
        )}
      >
        <div
          className={clsx(
            styles.impactContent,
            "d-flex flex-column align-items-start justify-content-center ",
          )}
        >
          <h2 className={"primary-title text-clr-gray mb-0"}>Biomaterials</h2>
          <h2 className={"primary-title mb-0"}>aren’t optional anymore</h2>

          <div className={clsx(" d-flex gap-3 space-lg-mt")}>
            <STButton aria-label="Previous Impact" variant="icon" className="impact-prev">
              {Icons.arrowLeft}
            </STButton>
            <STButton aria-label="Next Impact" variant="icon" className="impact-next">
              {Icons.arrowRight}
            </STButton>
          </div>
        </div>

        <div className={clsx(styles.impactSlider, "overflow-hidden")}>
          <STSwiper
            slidesPerView={2.2}
            spaceBetween={24}
            prevClass=".impact-prev"
            nextClass=".impact-next"
            breakpoints={{
              0: { slidesPerView: 1.1 },
              575: { slidesPerView: 1.8 },
              768: { slidesPerView: 2.2 },
              992: { slidesPerView: 1.6 },
              1200: { slidesPerView: 2.2 },
            }}
            autoplay
            loop
          >
            {impactData.map((item, index) => (
              <SwiperSlide key={index}>
                <div
                  className={clsx(
                    styles.impactCard,
                    "bg-off-white radius-lg space-xs-p h-100",
                  )}
                >
                  <div className={clsx(styles.impactImage, "ratio")}>
                    <Image
                      src={item.img}
                      alt={item.title}
                      className="object-fit-cover"
                    />
                  </div>

                  <div className={clsx(styles.impactCardBody)}>
                    <h3 className={"secondary-title fw-normal mb-3"}>
                      {item.title}
                    </h3>
                    <p className={"mb-0 paragraph-text"}>{item.desc}</p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </STSwiper>
        </div>
      </section>
      {/* IMPACT SECTION END */}

      {/* LOGO MARQUEE SECTION START */}
      <section
        className={clsx(styles.logoMarqueeSection, "bg-off-white space-lg-py")}
      >
        {/* ROW 1 → LEFT TO RIGHT */}
        <div className={clsx(styles.marqueeContainer, "space-md-mb")}>
          <div className={clsx(styles.marqueeContent, styles.marqueeLeft)}>
            {[...LOGOS, ...LOGOS, ...LOGOS].map((logo, index) => (
              <Image
                key={`top-${index}`}
                src={logo}
                alt="logo"
                className={clsx(styles.logoImg, "radius-xs")}
              />
            ))}
          </div>
        </div>

        {/* ROW 2 → RIGHT TO LEFT */}
        <div className={styles.marqueeContainer}>
          <div className={clsx(styles.marqueeContent, styles.marqueeRight)}>
            {[...LOGOS, ...LOGOS, ...LOGOS].map((logo, index) => (
              <Image
                key={`bottom-${index}`}
                src={logo}
                alt="logo"
                className={clsx(styles.logoImg, "radius-xs")}
              />
            ))}
          </div>
        </div>
      </section>
      {/* LOGO MARQUEE SECTION END */}

      {/* BLOG NEWS SECTION START */}
      <section className="space-xl-py">
        <div className="container space-md-mb">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="primary-title text-clr-gray mb-0">
              Blogs and <span className="text-clr-black">news</span>
            </h2>
            <div className="d-flex align-items-center gap-sm">
              <STButton aria-label="Previous Blog" variant="icon" className="blogs-prev">
                {Icons.arrowLeft}
              </STButton>
              <STButton aria-label="Next Blog" variant="icon" className="blogs-next">
                {Icons.arrowRight}
              </STButton>
            </div>
          </div>
        </div>
        <STSwiper
          slidesPerView={2.8}
          spaceBetween={24}
          prevClass=".blogs-prev"
          nextClass=".blogs-next"
          className={styles.blogsSwiper}
          breakpoints={{
            0: { slidesPerView: 1.1 },
            768: { slidesPerView: 1.8 },
            1200: { slidesPerView: 2.5 },
            1440: { slidesPerView: 2.8 },
          }}
        >
          {blogs && blogs.length > 0 ? (
            blogs.map((item) => (
              <SwiperSlide key={item.id} className={styles.blogSlide}>
                <div
                  className={clsx(
                    styles.blogCard,
                    "bg-off-white radius-lg h-100",
                  )}
                >
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start space-sm-mb gap-xs">
                    <div
                      className={clsx(
                        styles.blogImageWrapper,
                        "ratio ratio-1x1 rounded-circle overflow-hidden",
                      )}
                    >
                      {item.image ? (
                        <Image
                          src={item.image}
                          alt={item.name || item.title}
                          fill
                          className={"object-fit-cover"}
                        />
                      ) : (
                        <div className="bg-secondary w-100 h-100 d-flex align-items-center justify-content-center text-white">
                          No Image
                        </div>
                      )}
                    </div>

                    <p
                      className={clsx(
                        styles.blogTag,
                        "bg-white mb-0 text-uppercase",
                      )}
                    >
                      {item.category || "General"}
                    </p>
                  </div>

                  <h3
                    className={
                      "secondary-title space-sm-mb text-ellipsis line-clamp-2"
                    }
                  >
                    {item.name || item.title}
                  </h3>

                  <Link
                    href={`/news-feed/${item.slug}`}
                    className={clsx(
                      styles.blogLink,
                      "fw-medium text-clr-blue d-inline-flex align-items-center gap-2",
                    )}
                    aria-label={`Read more about ${item.name || item.title}`}
                  >
                    Read more
                    <span className="visually-hidden">
                      {` about ${item.name || item.title}`}
                    </span>
                    <span className="transition-smooth ms-2">
                      {Icons.arrowDoubleRightIcon}
                    </span>
                  </Link>
                </div>
              </SwiperSlide>
            ))
          ) : (
            // Fallback to static data if API fails or returns empty during development
            blogsNews.map((item) => (
              <SwiperSlide key={item.id} className={styles.blogSlide}>
                <div
                  className={clsx(
                    styles.blogCard,
                    "bg-off-white radius-lg h-100",
                  )}
                >
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start space-sm-mb gap-xs">
                    <div
                      className={clsx(
                        styles.blogImageWrapper,
                        "ratio ratio-1x1 rounded-circle overflow-hidden",
                      )}
                    >
                      <Image
                        src={item.image}
                        alt={item.title}
                        className={"object-fit-cover"}
                      />
                    </div>

                    <p
                      className={clsx(
                        styles.blogTag,
                        "bg-white mb-0 text-uppercase",
                      )}
                    >
                      {item.tag}
                    </p>
                  </div>

                  <h3
                    className={
                      "secondary-title space-sm-mb text-ellipsis line-clamp-2"
                    }
                  >
                    {item.title}
                  </h3>

                  <Link
                    href={item.href}
                    className={clsx(
                      styles.blogLink,
                      "fw-medium text-clr-blue d-inline-flex align-items-center gap-2",
                    )}
                    aria-label={`Read more about ${item.title}`}
                  >
                    Read more
                    <span className="visually-hidden">
                      {` about ${item.title}`}
                    </span>
                    <span className="transition-smooth ms-2">
                      {Icons.arrowDoubleRightIcon}
                    </span>
                  </Link>
                </div>
              </SwiperSlide>
            ))
          )}
        </STSwiper>
      </section>
      {/* BLOG NEWS SECTION END */}

      {/* LET'S TALK SECTION START */}
      <section className="space-md-py bg-clr-black">
        <div className="container">
          <div
            className={clsx(
              styles.letsTalkWrapper,
              "d-flex flex-column flex-xl-row align-items-center gap-lg",
            )}
          >
            {/* image */}
            <div
              className={clsx(
                styles.letsTalkImageWrapper,
                "w-100 rounded-circle overflow-hidden ratio ratio-1x1",
              )}
            >
              <Image
                src={letsTalkImg}
                alt="Let's Talk"
                className={"object-fit-cover w-100 h-100"}
              />
            </div>
            {/* content  */}
            <div
              className={clsx(styles.letsTalkContent, "w-100 text-clr-white")}
            >
              <h2
                className={clsx(
                  styles.letsTalkTitle,
                  "primary-title space-xs-mb text-clr-white",
                )}
              >
                <span>Let’s</span> talk
              </h2>

              <div className="d-flex flex-column gap-md align-items-start space-md-mt">
                {success && (
                  <p className="text-success mb-0">
                    Message sent successfully!
                  </p>
                )}
                <div
                  className={clsx(
                    styles.letsTalkFormTopRow,
                    "d-grid gap-md w-100",
                  )}
                >
                  <STSelect
                    placeholder="I am enquiring as a"
                    options={[
                      {
                        label: "Industry associate",
                        value: "Industry associate",
                      },
                      { label: "Investor", value: "Investor" },
                      { label: "Individual", value: "Individual" },
                    ]}
                    variant="dark"
                    value={formData.type}
                    onChange={handleSelectChange}
                    error={errors.type}
                  />
                  <STInput
                    placeholder="Name"
                    variant="dark"
                    className={"w-100"}
                    name="name"
                    value={formData.name}
                    handleChange={handleChange}
                    error={errors.name}
                  />

                  <STInput
                    placeholder="Email address"
                    variant="dark"
                    className={"w-100"}
                    name="email"
                    value={formData.email}
                    handleChange={handleChange}
                    error={errors.email}
                  />
                </div>
                <div
                  className={clsx(
                    styles.bottomRow,
                    "d-flex gap-md w-100 align-items-start align-items-sm-center flex-column flex-sm-row",
                  )}
                >
                  <STInput
                    placeholder="Share your thoughts with us..."
                    variant="dark"
                    className="w-100"
                    name="message"
                    value={formData.message}
                    handleChange={handleChange}
                    error={errors.message}
                  />

                  <STButton
                    variant="white"
                    suffix={Icons.arrowRight}
                    className={clsx(styles.getInTouchBtn, "flex-shrink-0")}
                    onClick={handleSubmit}
                    loading={loading}
                  >
                    Connect
                  </STButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* LET'S TALK SECTION END */}

      <STDrawer
        open={bioMassDrawerOpen}
        onClose={() => setBioMassDrawerOpen(false)}
        position="right"
      >
        <p className="paragraph-text h6 space-xs-mb">
          Total sustainable biomass potential in the EU is estimated between 130
          and 260 Mtoe (Million Tonnes of Oil Equivalent). Lignocellulosic
          biomass accounts for over 80% of this technical potential.
        </p>

        <p className="paragraph-text h6 space-xs-mb">
          Forestry Residues (Lignocellulosic): The largest category, comprising
          primary residues (branches, treetops) and secondary residues (sawdust,
          bark from mills).
        </p>
        <p className="paragraph-text h6 space-xs-mb">
          Agricultural Residues (Lignocellulosic): Primarily cereal straw, corn
          stover, and sunflower stalks.
        </p>
        <p className="paragraph-text h6 space-xs-mb">
          France, Germany, Sweden, Finland and Romania are the region with
          biggest availability. The resource that is as local as it can get.
        </p>
        <p className="paragraph-text h6 mb-0">
          Global bio-polymer capacity is growing at a 13–18% CAGR
        </p>
      </STDrawer>

      {/* Bio Material Drawer Start */}
      <STDrawer
        open={bioMaterialDrawerOpen}
        onClose={() => setBioMaterialDrawerOpen(false)}
      >
        <p className="paragraph-text h6 space-xs-mb">
          We are an advanced biomaterials start-up specializing in
          polysaccharide chemistry. The first product we have developed is a
          100% bio-based coating for paper packaging from 2nd generation
          agricultural biomass, providing a high-performance bio-alternative to
          microplastics, synthetic chemicals, and PFAS and expensive and
          non-functional bio-plastics like PLA, PHA etc.
        </p>
        <p className="paragraph-text h6 space-xs-mb">
          As an example, Sustanix materials aren't just sustainable—they are
          efficient. Sustainability is the by-product, Performance is the
          product. We are leveraging the complex architecture of nature to solve
          engineering challenges that petroleum cannot, positioning Sustanix as
          an essential partner in the transition to a high-performance circular
          economy.
        </p>
        <p className="paragraph-text h6 mb-0">
          The products in the future will be novel just like this one, but the
          underlying principle will remain the same, that is, develop
          biomaterials that match or exceed fossil-based materials without
          compromising on scale, cost or efficiency.
        </p>
      </STDrawer>
      {/* Bio Material Drawer End */}
    </>
  );
};
